import { AddressAutocompleteInput, formatAddress } from '../../components/common/AddressAutocompleteInput';
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Input,
  Textarea,
  Switch,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  FormControl,
  FormLabel,
  FormHelperText,
  Chip,
  Alert,
  Select,
  Option,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemDecorator,
} from '@mui/joy';
import {
  FloppyDisk as SaveIcon,
  Buildings as BusinessIcon,
  Palette as DesignIcon,
  MagnifyingGlass as SEOIcon,
  Gauge as PerformanceIcon,
  ShieldCheck as SecurityIcon,
  Plus as AddIcon,
  Trash as DeleteIcon,
  Phone as PhoneIcon,
  Envelope as EmailIcon,
  MapPin as LocationIcon,
  Clock as HoursIcon,
  Info as InfoIcon,
  HardDrives as DnsIcon,
  CloudArrowUp as DeploymentIcon,
  List as MenuIcon,
  Sparkle as AIIcon,
  CaretDown as ExpandMore,
  CaretRight as ChevronRight,
  ArrowElbowDownRight as SubdirectoryArrowRight,
} from '@phosphor-icons/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { DomainManager } from '../../components/cms/DomainManager';
import { TemplateSelector } from '../../components/cms/TemplateSelector';
import { HelpTooltip } from '../../components/cms/HelpTooltip';
import { SuccessMessage } from '../../components/cms/SuccessMessage';
import { DragDropList } from '../../components/cms/DragDropList';
import { ColorPicker } from '../../components/cms/ColorPicker';
import type { AreaServed, OrganizationOverview } from '../../types';

interface NavigationItem {
  label: string;
  url: string;
  pageId?: number | null;
  type: 'page' | 'external' | 'section';
  children?: NavigationItem[];
}

interface NavigationEditorProps {
  items: NavigationItem[];
  sitePages: any[];
  onReorder: (items: NavigationItem[]) => void;
  onItemChange: (path: number[], field: string, value: any) => void;
  onRemoveItem: (path: number[]) => void;
  onAddSubItem: (parentPath: number[]) => void;
}

const NavigationEditor: React.FC<NavigationEditorProps> = ({
  items,
  sitePages,
  onReorder,
  onItemChange,
  onRemoveItem,
  onAddSubItem,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpanded = (path: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const renderNavItem = (item: NavigationItem, index: number, path: number[] = [], depth: number = 0, isDragging?: boolean) => {
    const currentPath = [...path, index];
    const pathKey = currentPath.join('-');
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(pathKey);

    return (
      <Box key={pathKey}>
        <Card variant="outlined" sx={{ ml: depth > 0 ? 4 : 0, mb: 1, opacity: isDragging ? 0.5 : 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              {hasChildren && (
                <IconButton 
                  size="sm" 
                  onClick={() => toggleExpanded(pathKey)}
                  sx={{ p: 0.5 }}
                >
                  {isExpanded ? <ExpandMore /> : <ChevronRight />}
                </IconButton>
              )}
              {depth > 0 && !hasChildren && (
                <SubdirectoryArrowRight style={{ marginLeft: 8 }} color="var(--joy-palette-neutral-400)" />
              )}
              
              <Box sx={{ flex: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 150 }}>
                  <FormLabel>Label</FormLabel>
                  <Input
                    size="sm"
                    value={item.label}
                    onChange={(e) => onItemChange(currentPath, 'label', e.target.value)}
                    placeholder="Menu Label"
                  />
                </FormControl>
                
                <FormControl sx={{ minWidth: 120 }}>
                  <FormLabel>Type</FormLabel>
                  <Select
                    size="sm"
                    value={item.type}
                    onChange={(_, value) => onItemChange(currentPath, 'type', value)}
                  >
                    <Option value="page">Page</Option>
                    <Option value="external">External</Option>
                    <Option value="section">Section</Option>
                  </Select>
                </FormControl>
                
                {item.type === 'page' ? (
                  <FormControl sx={{ minWidth: 200 }}>
                    <FormLabel>Page</FormLabel>
                    <Select
                      size="sm"
                      value={item.pageId ? String(item.pageId) : ''}
                      onChange={(_, value) => onItemChange(currentPath, 'pageId', value ? Number(value) : null)}
                      placeholder="Select a page"
                    >
                      <Option value="">-- Select Page --</Option>
                      {sitePages.map((page: any) => (
                        <Option key={page.id} value={String(page.id)}>
                          {page.seo_draft?.title || page.title || page.slug}
                          {page.slug === 'index' && ' (Home)'}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <FormControl sx={{ minWidth: 200 }}>
                    <FormLabel>URL</FormLabel>
                    <Input
                      size="sm"
                      value={item.url}
                      onChange={(e) => onItemChange(currentPath, 'url', e.target.value)}
                      placeholder={item.type === 'external' ? "https://..." : "#section-id"}
                    />
                  </FormControl>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {depth < 2 && ( // Limit nesting to 2 levels
                  <Button
                    size="sm"
                    variant="plain"
                    onClick={() => onAddSubItem(currentPath)}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    <AddIcon size={18} /> Sub
                  </Button>
                )}
                <IconButton
                  size="sm"
                  color="danger"
                  onClick={() => onRemoveItem(currentPath)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        {hasChildren && isExpanded && !isDragging && (
          <Box>
            {item.children!.map((child, childIndex) => 
              renderNavItem(child, childIndex, currentPath, depth + 1)
            )}
          </Box>
        )}
      </Box>
    );
  };

  // Use DragDropList for top-level items
  return (
    <DragDropList
      items={items}
      onReorder={onReorder}
      renderItem={(item: NavigationItem, index: number, isDragging?: boolean) => 
        renderNavItem(item, index, [], 0, isDragging)
      }
      keyExtractor={(item: NavigationItem) => item.label + item.url}
      containerProps={{ sx: { gap: 1 } }}
    />
  );
};

// Available fonts for typography settings
const AVAILABLE_FONTS = [
  { value: 'Inter', label: 'Inter' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Lato', label: 'Lato' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Playfair Display', label: 'Playfair Display' },
  { value: 'Merriweather', label: 'Merriweather' },
  { value: 'Source Sans Pro', label: 'Source Sans Pro' },
  { value: 'Raleway', label: 'Raleway' },
];

interface SiteSettingsEnhancedProps {
  siteId: number;
}

export const SiteSettingsEnhanced: React.FC<SiteSettingsEnhancedProps> = ({ siteId }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [settings, setSettings] = useState<any>({});
  const [formData, setFormData] = useState<any>({});
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Load site data
  const { data: site, isLoading } = useQuery({
    queryKey: ['cms-site', siteId],
    queryFn: async () => {
      const response = await api.get(`/api/cms/sites/${siteId}/`);
      return response.data || response;
    },
  });

  // Load organization service areas from Knowledge Graph
  const { data: organizationAreas, isLoading: areasLoading } = useQuery({
    queryKey: ['areas-served'],
    queryFn: async () => {
      const response = await api.get<AreaServed[]>('/api/areas-served/');
      return response.data || [];
    },
  });

  // Fetch all pages for navigation dropdown
  const { data: sitePages } = useQuery({
    queryKey: ['cms-pages', siteId],
    queryFn: async () => {
      const response = await api.get(`/api/cms/sites/${siteId}/pages/`);
      const data = response.data || response;
      return Array.isArray(data) ? data : ((data as any).results || []);
    },
    enabled: !!siteId,
  });

  // Fetch organization branding (brand colors)
  const { data: organizationBranding } = useQuery({
    queryKey: ['organization-overview'],
    queryFn: async () => {
      const response = await api.get<OrganizationOverview>('/api/marketing/organization-overview/');
      return response.data;
    },
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes
  });

  // Transform organization colors to brandColors format for ColorPicker
  const brandColors = organizationBranding ? {
    primary: organizationBranding.brand_color_1 ? `#${organizationBranding.brand_color_1}` : undefined,
    secondary: organizationBranding.brand_color_2 ? `#${organizationBranding.brand_color_2}` : undefined,
    tertiary: organizationBranding.brand_color_3 ? `#${organizationBranding.brand_color_3}` : undefined,
    quaternary: organizationBranding.brand_color_4 ? `#${organizationBranding.brand_color_4}` : undefined,
    quinary: organizationBranding.brand_color_5 ? `#${organizationBranding.brand_color_5}` : undefined,
  } : undefined;

  // Save settings mutation
  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      // Separate top-level fields from nested settings
      const { name, domain, is_active, theme_key, ...nestedSettings } = data;
      
      // Build the payload with correct structure
      const payload = {
        name,
        domain,
        is_active,
        theme_key,  // Include theme_key at top level
        settings: nestedSettings  // Everything else goes in settings
      };
      
      console.log('Sending payload:', payload);
      return api.patch(`/api/cms/sites/${siteId}/`, payload);
    },
    onSuccess: () => {
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    },
  });

  const handleSave = () => {
    console.log('Saving settings:', settings);
    console.log('theme_key value:', settings.theme_key);
    saveMutation.mutate(settings);
  };

  const updateSetting = (path: string, value: any) => {
    const keys = path.split('.');
    const newSettings = { ...settings };
    let current = newSettings;
    
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    setSettings(newSettings);
  };

  // Navigation management functions
  const handleAddNavItem = (parentPath?: number[]) => {
    const currentNav = settings.navigation || [];
    const newItem = {
      label: 'New Item',
      url: '/',
      pageId: null,
      type: 'page' as const,
      children: [],
    };
    
    if (parentPath && parentPath.length > 0) {
      // Add as sub-item
      const newNav = [...currentNav];
      let parent = newNav[parentPath[0]];
      
      for (let i = 1; i < parentPath.length; i++) {
        parent = parent.children[parentPath[i]];
      }
      
      if (!parent.children) {
        parent.children = [];
      }
      parent.children.push(newItem);
      updateSetting('navigation', newNav);
    } else {
      // Add as top-level item
      updateSetting('navigation', [...currentNav, newItem]);
    }
  };

  const handleNavItemChange = (path: number[], field: string, value: any) => {
    const currentNav = [...(settings.navigation || [])];
    let item = currentNav[path[0]];
    
    for (let i = 1; i < path.length; i++) {
      item = item.children[path[i]];
    }
    
    item[field] = value;
    
    // If type is 'page' and pageId is set, update the URL
    if (field === 'pageId' && value && item.type === 'page') {
      const page = sitePages?.find((p: any) => p.id === value);
      if (page) {
        item.url = page.slug === 'index' ? '/' : `/${page.slug}`;
      }
    }
    
    updateSetting('navigation', currentNav);
  };

  const handleRemoveNavItem = (path: number[]) => {
    const currentNav = [...(settings.navigation || [])];
    
    if (path.length === 1) {
      currentNav.splice(path[0], 1);
    } else {
      let parent = currentNav[path[0]];
      for (let i = 1; i < path.length - 1; i++) {
        parent = parent.children[path[i]];
      }
      parent.children.splice(path[path.length - 1], 1);
    }
    
    updateSetting('navigation', currentNav);
  };

  const handleReorderNavigation = (reorderedItems: any[]) => {
    updateSetting('navigation', reorderedItems);
  };

  const handleGenerateNavigation = async () => {
    // Generate default navigation based on existing pages
    const defaultNav = [];
    
    // Add home page if it exists
    const homePage = sitePages?.find((p: any) => p.slug === 'index');
    if (homePage) {
      defaultNav.push({
        label: 'Home',
        url: '/',
        pageId: homePage.id,
        type: 'page',
        children: [],
      });
    }
    
    // Add other common pages
    const commonPages = ['about', 'services', 'contact'];
    commonPages.forEach(slug => {
      const page = sitePages?.find((p: any) => p.slug === slug);
      if (page) {
        const label = page.seo_draft?.title || page.title || slug.charAt(0).toUpperCase() + slug.slice(1);
        defaultNav.push({
          label,
          url: `/${slug}`,
          pageId: page.id,
          type: 'page',
          children: [],
        });
      }
    });
    
    updateSetting('navigation', defaultNav);
  };

  // Service area management - no longer needed with dropdown
  // Keeping for potential future use

  React.useEffect(() => {
    if (site) {
      setSettings({
        name: (site as any).name,
        domain: (site as any).domain,
        is_active: (site as any).is_active,
        theme_key: (site as any).theme_key,  // Add theme_key as top-level field
        ...(site as any).settings,
      });
    }
  }, [site]);

  // Automatically sync service areas from Knowledge Graph
  React.useEffect(() => {
    if (organizationAreas && organizationAreas.length > 0) {
      const areaStrings = organizationAreas.map(area => `${area.city}, ${area.state}`);
      // Only update if different to avoid infinite loops
      const currentAreas = settings.business_info?.service_areas || [];
      if (JSON.stringify(currentAreas) !== JSON.stringify(areaStrings)) {
        updateSetting('business_info.service_areas', areaStrings);
      }
    }
  }, [organizationAreas]);

  if (isLoading) {
    return <Box>Loading...</Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography level="h2">Site Settings</Typography>
        <Button
          variant="solid"
          startDecorator={<SaveIcon />}
          onClick={handleSave}
          loading={saveMutation.isPending}
        >
          Save All Changes
        </Button>
      </Box>

      <Tabs value={activeTab} onChange={(event, value) => setActiveTab(value as number)}>
        <TabList>
          <Tab><BusinessIcon style={{ marginRight: 8 }} /> Business Info</Tab>
          <Tab><DesignIcon style={{ marginRight: 8 }} /> Design & Branding</Tab>
          <Tab><MenuIcon style={{ marginRight: 8 }} /> Navigation & Footer</Tab>
          <Tab><SEOIcon style={{ marginRight: 8 }} /> SEO Settings</Tab>
          <Tab><DnsIcon style={{ marginRight: 8 }} /> Domains</Tab>
          <Tab><DeploymentIcon style={{ marginRight: 8 }} /> Deployment</Tab>
          <Tab><PerformanceIcon style={{ marginRight: 8 }} /> Performance</Tab>
          <Tab><SecurityIcon style={{ marginRight: 8 }} /> Advanced</Tab>
        </TabList>

        {/* Business Info Tab */}
        <TabPanel value={0}>
          <Grid container spacing={3}>
            <Grid xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography level="title-lg" sx={{ mb: 3 }}>
                    Basic Information
                  </Typography>

                  <FormControl sx={{ mb: 3 }}>
                    <FormLabel>
                      Business Name
                      <HelpTooltip title="Business Name" content="This is your company name that will appear throughout your website and in search results." />
                    </FormLabel>
                    <Input
                      value={settings.name || ''}
                      onChange={(e) => updateSetting('name', e.target.value)}
                      placeholder="Your Roofing Company"
                    />
                  </FormControl>

                  <FormControl sx={{ mb: 3 }}>
                    <FormLabel>
                      Primary Domain
                      <HelpTooltip title="Primary Domain" content="Your main website domain. You can add additional custom domains in the Domains tab." />
                    </FormLabel>
                    <Input
                      value={settings.domain || ''}
                      onChange={(e) => updateSetting('domain', e.target.value)}
                      placeholder="yourcompany.com"
                      disabled
                      endDecorator={
                        <Chip size="sm" variant="soft" color="success">
                          Verified
                        </Chip>
                      }
                    />
                  </FormControl>

                  <FormControl sx={{ mb: 3 }}>
                    <FormLabel>
                      Phone Number
                      <HelpTooltip title="Phone Number" content="Your business phone number. This will be displayed on your website and used for contact forms." />
                    </FormLabel>
                    <Input
                      startDecorator={<PhoneIcon />}
                      value={settings.business_info?.phone || ''}
                      onChange={(e) => updateSetting('business_info.phone', e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                  </FormControl>

                  <FormControl sx={{ mb: 3 }}>
                    <FormLabel>
                      Email
                      <HelpTooltip title="Email" content="Your business email address. This will be used for contact forms and displayed on your website." />
                    </FormLabel>
                    <Input
                      startDecorator={<EmailIcon />}
                      type="email"
                      value={settings.business_info?.email || ''}
                      onChange={(e) => updateSetting('business_info.email', e.target.value)}
                      placeholder="info@yourcompany.com"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Business Address</FormLabel>
                    <AddressAutocompleteInput
                      value={settings.business_info?.address || ''}
                      onChange={(value) => updateSetting('business_info.address', value)}
                      onAddressSelect={(parsed) => updateSetting('business_info.address', formatAddress(parsed))}
                    />
                  </FormControl>
                </CardContent>
              </Card>
            </Grid>

            <Grid xs={12} md={6}>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography level="title-lg" sx={{ mb: 3 }}>
                    Business Hours
                  </Typography>

                  <Textarea
                    startDecorator={<HoursIcon />}
                    value={settings.business_info?.business_hours || ''}
                    onChange={(e) => updateSetting('business_info.business_hours', e.target.value)}
                    placeholder="Monday-Friday: 8:00 AM - 6:00 PM&#10;Saturday: 9:00 AM - 4:00 PM&#10;Sunday: Closed"
                    minRows={4}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography level="title-lg" sx={{ mb: 3 }}>
                    Service Areas
                  </Typography>

                  <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
                    Service areas are managed in your{' '}
                    <a href="/settings/organization" target="_blank" rel="noopener noreferrer">
                      organization settings
                    </a>
                    . All defined service areas are automatically included on this site.
                  </Typography>

                  {organizationAreas && organizationAreas.length > 0 ? (
                    <List size="sm">
                      {organizationAreas.map((area) => (
                        <ListItem key={area.id}>
                          <ListItemDecorator>
                            <LocationIcon />
                          </ListItemDecorator>
                          <Typography>{area.city}, {area.state}</Typography>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Alert variant="soft" startDecorator={<InfoIcon />}>
                      No service areas defined yet.{' '}
                      <a href="/settings/organization" target="_blank" rel="noopener noreferrer">
                        Add service areas
                      </a>{' '}
                      to help with local SEO and show where you provide services.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Design & Branding Tab */}
        <TabPanel value={1}>
          <Grid container spacing={3}>
            <Grid xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography level="title-lg" sx={{ mb: 3 }}>
                    Theme Selection
                  </Typography>

                  <FormControl sx={{ mb: 3 }}>
                    <FormLabel>
                      Website Theme
                      <HelpTooltip title="Website Theme" content="Choose a theme that matches your brand. Each theme has a different look and feel for your website." />
                    </FormLabel>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setShowTemplateSelector(true)}
                      sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                    >
                      {settings.theme_key ? 
                        `${settings.theme_key.charAt(0).toUpperCase() + settings.theme_key.slice(1)} Theme` : 
                        'Select Theme'
                      }
                    </Button>
                  </FormControl>

                  <Alert variant="soft">
                    Changing themes will update the look of your entire website.
                    Your content will remain the same.
                  </Alert>
                </CardContent>
              </Card>
            </Grid>

            <Grid xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography level="title-lg" sx={{ mb: 2 }}>
                    Brand Colors
                  </Typography>
                  <Typography level="body-sm" sx={{ mb: 3, color: 'text.secondary' }}>
                    Brand colors are managed in your{' '}
                    <a href="/settings/organization" style={{ color: 'inherit', textDecoration: 'underline' }}>
                      organization settings
                    </a>.
                  </Typography>

                  {organizationBranding && (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {organizationBranding.brand_color_1 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 'sm',
                            bgcolor: `#${organizationBranding.brand_color_1}`,
                            border: '1px solid',
                            borderColor: 'divider'
                          }} />
                          <Typography level="body-sm">Primary</Typography>
                        </Box>
                      )}
                      {organizationBranding.brand_color_2 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 'sm',
                            bgcolor: `#${organizationBranding.brand_color_2}`,
                            border: '1px solid',
                            borderColor: 'divider'
                          }} />
                          <Typography level="body-sm">Secondary</Typography>
                        </Box>
                      )}
                      {organizationBranding.brand_color_3 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 'sm',
                            bgcolor: `#${organizationBranding.brand_color_3}`,
                            border: '1px solid',
                            borderColor: 'divider'
                          }} />
                          <Typography level="body-sm">Accent 1</Typography>
                        </Box>
                      )}
                      {organizationBranding.brand_color_4 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 'sm',
                            bgcolor: `#${organizationBranding.brand_color_4}`,
                            border: '1px solid',
                            borderColor: 'divider'
                          }} />
                          <Typography level="body-sm">Accent 2</Typography>
                        </Box>
                      )}
                      {organizationBranding.brand_color_5 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 'sm',
                            bgcolor: `#${organizationBranding.brand_color_5}`,
                            border: '1px solid',
                            borderColor: 'divider'
                          }} />
                          <Typography level="body-sm">Accent 3</Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  {!organizationBranding && (
                    <Typography level="body-sm" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                      No brand colors configured yet.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Typography Settings */}
            <Grid xs={12}>
              <Card>
                <CardContent>
                  <Typography level="title-lg" sx={{ mb: 2 }}>
                    Typography Settings
                  </Typography>
                  <Typography level="body-sm" sx={{ mb: 3, color: 'text.secondary' }}>
                    Set default colors and fonts for headings and body text. Individual content blocks can override these settings.
                  </Typography>

                  <Grid container spacing={2}>
                    {/* H1 Settings */}
                    <Grid xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography level="title-sm" sx={{ mb: 2 }}>H1 - Main Headings</Typography>
                          <FormControl sx={{ mb: 2 }}>
                            <FormLabel>Font</FormLabel>
                            <Select
                              value={settings.typography?.h1?.font || ''}
                              onChange={(_, value) => updateSetting('typography.h1.font', value)}
                              placeholder="Inherit from Default"
                            >
                              <Option value="">Inherit from Default</Option>
                              {AVAILABLE_FONTS.map((font) => (
                                <Option key={font.value} value={font.value}>{font.label}</Option>
                              ))}
                            </Select>
                          </FormControl>
                          <ColorPicker
                            value={settings.typography?.h1?.color || ''}
                            onChange={(color) => updateSetting('typography.h1.color', color)}
                            label="Color"
                            helpText="Choose from brand colors or pick a custom color"
                            brandColors={brandColors}
                          />
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* H2 Settings */}
                    <Grid xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography level="title-sm" sx={{ mb: 2 }}>H2 - Section Headings</Typography>
                          <FormControl sx={{ mb: 2 }}>
                            <FormLabel>Font</FormLabel>
                            <Select
                              value={settings.typography?.h2?.font || ''}
                              onChange={(_, value) => updateSetting('typography.h2.font', value)}
                              placeholder="Inherit from Default"
                            >
                              <Option value="">Inherit from Default</Option>
                              {AVAILABLE_FONTS.map((font) => (
                                <Option key={font.value} value={font.value}>{font.label}</Option>
                              ))}
                            </Select>
                          </FormControl>
                          <ColorPicker
                            value={settings.typography?.h2?.color || ''}
                            onChange={(color) => updateSetting('typography.h2.color', color)}
                            label="Color"
                            helpText="Choose from brand colors or pick a custom color"
                            brandColors={brandColors}
                          />
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* H3 Settings */}
                    <Grid xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography level="title-sm" sx={{ mb: 2 }}>H3 - Subsection Headings</Typography>
                          <FormControl sx={{ mb: 2 }}>
                            <FormLabel>Font</FormLabel>
                            <Select
                              value={settings.typography?.h3?.font || ''}
                              onChange={(_, value) => updateSetting('typography.h3.font', value)}
                              placeholder="Inherit from Default"
                            >
                              <Option value="">Inherit from Default</Option>
                              {AVAILABLE_FONTS.map((font) => (
                                <Option key={font.value} value={font.value}>{font.label}</Option>
                              ))}
                            </Select>
                          </FormControl>
                          <ColorPicker
                            value={settings.typography?.h3?.color || ''}
                            onChange={(color) => updateSetting('typography.h3.color', color)}
                            label="Color"
                            helpText="Choose from brand colors or pick a custom color"
                            brandColors={brandColors}
                          />
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Body Text Settings */}
                    <Grid xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography level="title-sm" sx={{ mb: 2 }}>Body Text</Typography>
                          <FormControl sx={{ mb: 2 }}>
                            <FormLabel>Font</FormLabel>
                            <Select
                              value={settings.typography?.body?.font || ''}
                              onChange={(_, value) => updateSetting('typography.body.font', value)}
                              placeholder="Inherit from Default"
                            >
                              <Option value="">Inherit from Default</Option>
                              {AVAILABLE_FONTS.map((font) => (
                                <Option key={font.value} value={font.value}>{font.label}</Option>
                              ))}
                            </Select>
                          </FormControl>
                          <ColorPicker
                            value={settings.typography?.body?.color || ''}
                            onChange={(color) => updateSetting('typography.body.color', color)}
                            label="Color"
                            helpText="Choose from brand colors or pick a custom color"
                            brandColors={brandColors}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Navigation & Footer Tab */}
        <TabPanel value={2}>
          <Grid container spacing={3}>
            <Grid xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography level="title-lg">Navigation Menu</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="sm"
                        variant="outlined"
                        color="primary"
                        startDecorator={<AIIcon />}
                        onClick={handleGenerateNavigation}
                      >
                        Generate Navigation
                      </Button>
                      <Button
                        size="sm"
                        startDecorator={<AddIcon />}
                        onClick={() => handleAddNavItem()}
                      >
                        Add Item
                      </Button>
                    </Box>
                  </Box>

                  {(!settings.navigation || settings.navigation.length === 0) ? (
                    <Alert variant="soft" color="neutral">
                      No navigation items added yet. Click "Add Item" to create your menu.
                    </Alert>
                  ) : (
                    <NavigationEditor
                      items={settings.navigation || []}
                      sitePages={sitePages || []}
                      onReorder={handleReorderNavigation}
                      onItemChange={handleNavItemChange}
                      onRemoveItem={handleRemoveNavItem}
                      onAddSubItem={handleAddNavItem}
                    />
                  )}

                  <Divider sx={{ my: 3 }} />

                  <Typography level="title-lg" sx={{ mb: 2 }}>Footer</Typography>
                  
                  <FormControl>
                    <FormLabel>Copyright Text</FormLabel>
                    <Input
                      value={settings.footer?.copyright || ''}
                      onChange={(e) => updateSetting('footer.copyright', e.target.value)}
                      placeholder={`© ${new Date().getFullYear()} Your Company. All rights reserved.`}
                    />
                  </FormControl>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* SEO Settings Tab */}
        <TabPanel value={3}>
          <Card>
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 3 }}>
                Search Engine Optimization
              </Typography>

              <FormControl sx={{ mb: 3 }}>
                <FormLabel>Default Page Title Suffix</FormLabel>
                <Input
                  value={settings.seo_defaults?.title_suffix || ''}
                  onChange={(e) => updateSetting('seo_defaults.title_suffix', e.target.value)}
                  placeholder=" | Your Roofing Company"
                />
                <FormHelperText>Added to the end of all page titles</FormHelperText>
              </FormControl>

              <FormControl sx={{ mb: 3 }}>
                <FormLabel>Default Meta Description</FormLabel>
                <Textarea
                  value={settings.seo_defaults?.description || ''}
                  onChange={(e) => updateSetting('seo_defaults.description', e.target.value)}
                  placeholder="Professional roofing services including repairs, replacements, and maintenance..."
                  minRows={3}
                />
                <FormHelperText>Used when pages don't have their own description</FormHelperText>
              </FormControl>

              <FormControl sx={{ mb: 3 }}>
                <FormLabel>Focus Keywords</FormLabel>
                <Input
                  value={settings.seo_defaults?.keywords || ''}
                  onChange={(e) => updateSetting('seo_defaults.keywords', e.target.value)}
                  placeholder="roofing, roof repair, roof replacement, roofing contractor"
                />
                <FormHelperText>Comma-separated list of your main keywords</FormHelperText>
              </FormControl>

              <Alert variant="soft" color="success">
                Good SEO settings help your website rank higher in Google searches.
                We automatically optimize your content based on these settings.
              </Alert>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Domains Tab */}
        <TabPanel value={4}>
          <DomainManager siteId={siteId} />
        </TabPanel>

        {/* Deployment Tab */}
        <TabPanel value={5}>
          <Grid container spacing={3}>
            <Grid xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography level="title-lg" sx={{ mb: 3 }}>
                    AWS Configuration
                  </Typography>

                  <FormControl sx={{ mb: 3 }}>
                    <FormLabel>S3 Bucket Name</FormLabel>
                    <Input
                      value={formData.s3_bucket || ''}
                      onChange={(e) => setFormData({ ...formData, s3_bucket: e.target.value })}
                      placeholder="my-website-bucket"
                    />
                    <FormHelperText>
                      S3 bucket where your website files will be stored
                    </FormHelperText>
                  </FormControl>

                  <FormControl sx={{ mb: 3 }}>
                    <FormLabel>AWS Region</FormLabel>
                    <Select
                      value={formData.aws_region || 'us-east-1'}
                      onChange={(_, value) => setFormData({ ...formData, aws_region: value })}
                    >
                      <Option value="us-east-1">US East (N. Virginia)</Option>
                      <Option value="us-west-2">US West (Oregon)</Option>
                      <Option value="eu-west-1">Europe (Ireland)</Option>
                      <Option value="ap-southeast-1">Asia Pacific (Singapore)</Option>
                    </Select>
                  </FormControl>

                  <FormControl sx={{ mb: 3 }}>
                    <FormLabel>CloudFront Distribution ID</FormLabel>
                    <Input
                      value={formData.cloudfront_distribution_id || ''}
                      onChange={(e) => setFormData({ ...formData, cloudfront_distribution_id: e.target.value })}
                      placeholder="E1234567890ABC"
                    />
                    <FormHelperText>
                      CloudFront distribution for CDN caching
                    </FormHelperText>
                  </FormControl>
                </CardContent>
              </Card>
            </Grid>

            <Grid xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography level="title-lg" sx={{ mb: 3 }}>
                    Deployment Settings
                  </Typography>

                  <FormControl sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <FormLabel>Auto-deploy on Content Changes</FormLabel>
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                          Automatically deploy when pages are published
                        </Typography>
                      </Box>
                      <Switch
                        checked={formData.auto_deploy || false}
                        onChange={(e) => setFormData({ ...formData, auto_deploy: e.target.checked })}
                      />
                    </Box>
                  </FormControl>

                  <FormControl sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <FormLabel>Enable Backup Before Deploy</FormLabel>
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                          Create backup before each deployment
                        </Typography>
                      </Box>
                      <Switch
                        checked={formData.backup_before_deploy !== false}
                        onChange={(e) => setFormData({ ...formData, backup_before_deploy: e.target.checked })}
                      />
                    </Box>
                  </FormControl>

                  <FormControl sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <FormLabel>Auto-rollback on Failure</FormLabel>
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                          Automatically rollback failed deployments
                        </Typography>
                      </Box>
                      <Switch
                        checked={formData.auto_rollback !== false}
                        onChange={(e) => setFormData({ ...formData, auto_rollback: e.target.checked })}
                      />
                    </Box>
                  </FormControl>

                  <Divider sx={{ my: 3 }} />

                  <Typography level="title-md" sx={{ mb: 2 }}>
                    Notification Settings
                  </Typography>

                  <FormControl sx={{ mb: 3 }}>
                    <FormLabel>Deployment Notification Email</FormLabel>
                    <Input
                      type="email"
                      value={formData.deployment_notification_email || ''}
                      onChange={(e) => setFormData({ ...formData, deployment_notification_email: e.target.value })}
                      placeholder="admin@yourcompany.com"
                    />
                    <FormHelperText>
                      Receive notifications about deployment status
                    </FormHelperText>
                  </FormControl>
                </CardContent>
              </Card>
            </Grid>

            <Grid xs={12}>
              <Alert color="primary">
                <Typography level="title-sm">Deployment Management</Typography>
                <Typography level="body-sm">
                  Use the deployment dashboard to manage your site deployments. 
                  <Button 
                    variant="outlined"
            color="primary" 
                    size="sm" 
                    sx={{ ml: 1 }}
                    onClick={() => navigate(`/cms/sites/${siteId}/deploy`)}
                  >
                    Go to Deployment Dashboard
                  </Button>
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Performance Tab */}
        <TabPanel value={6}>
          <Card>
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 3 }}>
                Performance Optimization
              </Typography>

              <FormControl sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <FormLabel>Lazy Load Images</FormLabel>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      Load images as users scroll for faster page speed
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.performance?.lazy_loading ?? true}
                    onChange={(e) => updateSetting('performance.lazy_loading', e.target.checked)}
                  />
                </Box>
              </FormControl>

              <Divider sx={{ my: 2 }} />

              <FormControl sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <FormLabel>Optimize Images</FormLabel>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      Automatically compress and resize images
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.performance?.optimize_images ?? true}
                    onChange={(e) => updateSetting('performance.optimize_images', e.target.checked)}
                  />
                </Box>
              </FormControl>

              <Divider sx={{ my: 2 }} />

              <FormControl>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <FormLabel>Enable Caching</FormLabel>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      Cache pages for lightning-fast loading
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.performance?.caching ?? true}
                    onChange={(e) => updateSetting('performance.caching', e.target.checked)}
                  />
                </Box>
              </FormControl>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Advanced Tab */}
        <TabPanel value={7}>
          <Card>
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 3 }}>
                Advanced Settings
              </Typography>

              <FormControl sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <FormLabel>Site Active</FormLabel>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      Make your website visible to visitors
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.is_active ?? false}
                    onChange={(e) => updateSetting('is_active', e.target.checked)}
                  />
                </Box>
              </FormControl>

              <Divider sx={{ my: 2 }} />

              <FormControl>
                <FormLabel>Google Analytics ID</FormLabel>
                <Input
                  value={settings.analytics?.google_analytics_id || ''}
                  onChange={(e) => updateSetting('analytics.google_analytics_id', e.target.value)}
                  placeholder="UA-XXXXXXXXX-X or G-XXXXXXXXXX"
                />
              </FormControl>
            </CardContent>
          </Card>
        </TabPanel>
      </Tabs>

      {/* Template Selector Modal */}
      <TemplateSelector
        mode="themes"
        open={showTemplateSelector}
        onClose={() => setShowTemplateSelector(false)}
        currentTheme={settings.theme_key || 'atlas'}
        onThemeSelect={(themeKey) => {
          // theme_key is a top-level field, not nested in settings
          setSettings((prev: any) => ({ ...prev, theme_key: themeKey }));
          setShowTemplateSelector(false);
        }}
      />

      {/* Success Message */}
      <SuccessMessage
        open={showSuccessMessage}
        onClose={() => setShowSuccessMessage(false)}
        title="Settings Saved!"
        message="Your site settings have been updated successfully."
      />
    </Box>
  );
};
